// P3-011: Skill trigger and execution system (GDD section 4.5.5)

import { ElementType, SkillEffectType } from '@/shared/types/common';
import type { SkillSnapshot } from '@/shared/types/phase-contracts';
import type { BattleAgent, ActiveBuff } from '../types/battle-internal';
import { useBattleStore } from '../stores/battleStore';
import { useBoardStore } from '../stores/boardStore';
import { BOARD_COLS, BOARD_ROWS } from '../data/board-constants';
import { MAX_HEAL_TURNS, MAX_BOARD_CHANGE_TILES } from '../data/skill-configs';
import { shuffle } from '@/shared/utils/random';

export interface SkillTriggerResult {
  agentId: string;
  agentName: string;
  skill: SkillSnapshot;
}

/**
 * Check which agents should trigger their skill based on match element.
 * An agent triggers when:
 *   - Match element === agent's elementAffinity
 *   - Agent's currentSkillCD === 0
 *   - Agent's skill is not disabled this turn
 */
export function checkSkillTriggers(
  matchElement: ElementType,
  team: BattleAgent[],
): SkillTriggerResult[] {
  const triggers: SkillTriggerResult[] = [];

  for (const agent of team) {
    if (
      agent.elementAffinity === matchElement &&
      agent.currentSkillCD === 0 &&
      !agent.skillDisabledThisTurn
    ) {
      triggers.push({
        agentId: agent.agentId,
        agentName: agent.name,
        skill: agent.activeSkill,
      });
    }
  }

  return triggers;
}

/**
 * Execute a skill's effects.
 * Dispatches by SkillEffectType and modifies battle/board state accordingly.
 */
export function executeSkill(trigger: SkillTriggerResult): void {
  const battleStore = useBattleStore.getState();
  const boardStore = useBoardStore.getState();

  for (const effect of trigger.skill.effects) {
    switch (effect.type) {
      case SkillEffectType.DAMAGE:
        // Direct damage to enemy
        battleStore.dealDamage(effect.value);
        break;

      case SkillEffectType.HEAL:
        // Extend deadline (restore turns)
        {
          const healAmount = Math.min(effect.value, MAX_HEAL_TURNS);
          battleStore.extendDeadline(healAmount);
        }
        break;

      case SkillEffectType.BUFF_ATK:
        // Add ATK buff
        if (effect.duration > 0) {
          const buff: ActiveBuff = {
            type: SkillEffectType.BUFF_ATK,
            value: effect.value,
            remainingTurns: effect.duration,
            target: effect.target,
            sourceAgentId: trigger.agentId,
          };
          battleStore.addBuff(buff);
        }
        break;

      case SkillEffectType.BUFF_DEF:
        // Add DEF buff (reduces overload penalty)
        if (effect.duration > 0) {
          const buff: ActiveBuff = {
            type: SkillEffectType.BUFF_DEF,
            value: effect.value,
            remainingTurns: effect.duration,
            target: effect.target,
            sourceAgentId: trigger.agentId,
          };
          battleStore.addBuff(buff);
        }
        break;

      case SkillEffectType.DEBUFF_ENEMY:
        // Add enemy debuff (takes more damage)
        if (effect.duration > 0) {
          const buff: ActiveBuff = {
            type: SkillEffectType.DEBUFF_ENEMY,
            value: effect.value,
            remainingTurns: effect.duration,
            target: 'enemy',
            sourceAgentId: trigger.agentId,
          };
          battleStore.addBuff(buff);
        }
        break;

      case SkillEffectType.BOARD_CHANGE:
        // Convert random tiles to this agent's element color
        {
          const agent = battleStore.team.find((a) => a.agentId === trigger.agentId);
          if (agent) {
            const targetElement = agent.elementAffinity;
            const maxTiles = Math.min(effect.value, MAX_BOARD_CHANGE_TILES);
            convertRandomTiles(boardStore, targetElement, maxTiles);
          }
        }
        break;

      case SkillEffectType.COOLDOWN_REDUCE:
        // Reduce all team skill CDs
        {
          const currentTeam = useBattleStore.getState().team;
          for (const agent of currentTeam) {
            if (agent.agentId !== trigger.agentId) {
              const newCD = Math.max(0, agent.currentSkillCD - effect.value);
              battleStore.setAgentSkillCD(agent.agentId, newCD);
            }
          }
        }
        break;
    }
  }

  // Reset skill CD after trigger
  battleStore.setAgentSkillCD(trigger.agentId, trigger.skill.cooldown);

  // Track stats
  battleStore.incrementStat('skillsTriggered');
}

/**
 * Convert random non-locked tiles to a target element.
 */
function convertRandomTiles(
  boardStore: ReturnType<typeof useBoardStore.getState>,
  targetElement: ElementType,
  count: number,
): void {
  const { tiles } = boardStore;

  // Collect all positions that can be converted
  const candidates: { x: number; y: number }[] = [];
  for (let x = 0; x < BOARD_COLS; x++) {
    for (let y = 0; y < BOARD_ROWS; y++) {
      const tile = tiles[x]?.[y];
      if (tile && !tile.locked && tile.element !== targetElement) {
        candidates.push({ x, y });
      }
    }
  }

  // Shuffle and pick up to count (Fisher-Yates via shared utility)
  const shuffled = shuffle([...candidates]);
  const toConvert = shuffled.slice(0, count);

  for (const pos of toConvert) {
    boardStore.setTileElement(pos, targetElement);
  }
}

/**
 * Process all skill triggers for a set of matches in a cascade step.
 * Returns all triggered skills.
 */
export function processSkillTriggersForMatches(
  matchElements: ElementType[],
): SkillTriggerResult[] {
  const team = useBattleStore.getState().team;
  const allTriggers: SkillTriggerResult[] = [];
  const triggeredAgentIds = new Set<string>();

  // Deduplicate: each agent can only trigger once per cascade step
  for (const element of matchElements) {
    const triggers = checkSkillTriggers(element, team);
    for (const trigger of triggers) {
      if (!triggeredAgentIds.has(trigger.agentId)) {
        triggeredAgentIds.add(trigger.agentId);
        allTriggers.push(trigger);
      }
    }
  }

  // Execute all triggered skills
  for (const trigger of allTriggers) {
    executeSkill(trigger);
  }

  return allTriggers;
}
